import { useState } from 'react';
import { useT } from '../i18n/locale-context';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { CompanyPrompt } from './company-prompt';

/**
 * The ИНН ask, as a modal opened from the catalog reminder strip.
 *
 * ## Why the «Позже» moved here from the strip
 *
 * The strip used to carry the dismissal itself, one tap from the label. That
 * asked people to decline before anything had explained what they were
 * declining — the row has room for three words, not for a reason. A dismissal
 * taken from someone who was never told the offer is not really a decision;
 * it is just the fastest way to make a nag stop.
 *
 * So the strip now only OPENS this, and the two answers live where the
 * explanation is: «Проверить» submits, «Позже» retires the reminder. Same
 * escape hatch, same one tap to reach it, but taken after reading three true
 * sentences about what the ИНН buys.
 *
 * ## What «Позже» does and does not do
 *
 * It silences the reminders — this modal and the strip's row. It does NOT
 * remove the form: `CompanyPrompt` is mounted permanently and undismissibly in
 * settings, so "later" stays literally true. That separation is the whole
 * reason this component passes `onSkip` and settings does not.
 */
export function CompanyInnModal({
  open,
  onClose,
  onLater,
}: {
  open: boolean;
  /** Dismiss WITHOUT answering — overlay click, Esc, the X. The step stays
   *  pending and will be reminded about again. */
  onClose: () => void;
  /** «Позже» — an actual answer. Retires the step from every reminder. */
  onLater: () => void;
}) {
  const t = useT();
  // The heading has to stop asking once it has been answered. Leaving
  // «Укажите ИНН вашей компании» above a success message reads as a form that
  // failed to submit — the one impression this screen must not leave, since it
  // is the payoff that justifies asking for a government identifier at all.
  const [resolved, setResolved] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        mobileVariant="sheet"
        /* `bg-bone-surface`, not the primitive's default `bg-cream`. Every card
           in the app sits on bone-surface; a cream panel reads as a different
           component library dropped into the page. Overridden per-instance
           rather than in the DS primitive so this stays a decision about THIS
           dialog. */
        className="max-w-md border-edge bg-bone-surface p-6"
      >
        {/* The visible heading, owned by the dialog. CompanyPrompt suppresses
            its own when `bare`: rendering both put the same sentence in the
            accessibility tree twice — once as the dialog's label, once as a
            heading — so a screen reader announced it on open and again on
            entering the content. */}
        <DialogTitle className="text-[17px] font-bold leading-snug text-ink">
          {resolved ? t('company.savedTitle') : t('company.title')}
        </DialogTitle>
        {/* Radix warns without one, and the warning is right: a screen reader
            announcing only «Укажите ИНН вашей компании» gives no reason for the
            ask. Visually hidden — the bullets below say the same thing to a
            sighted reader, and repeating them here would be a second copy. */}
        <DialogDescription className="sr-only">{t('company.subtitle')}</DialogDescription>
        {/* The form, the payoff and the benefits all come from CompanyPrompt —
            there is exactly ONE ИНН flow in the app, and this is a frame around
            it. A second copy here would be the fourth surface with its own
            opinion about the ИНН step, which is the defect the shared step
            model was introduced to end. */}
        {/* No `onDone` — the modal deliberately stays open after a successful
            submit. Closing on success would destroy the payoff («ваша компания
            выиграла N тендеров»), which is the entire return the user gets for
            handing over a government identifier. It is the same defect that
            once made this card unmount itself the instant it succeeded. */}
        <CompanyPrompt
          bare
          benefits
          surface="modal"
          onSkip={onLater}
          onResolved={() => setResolved(true)}
          onFinish={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
